import { useState, useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import AutoCompleteHeader from '../../modules/AutoCompleteHeader'
import { Settings } from './settings/Settings'
import { Skeleton, CircularProgress, Tooltip } from '@mui/material'
import UpdateIcon from '@mui/icons-material/CachedOutlined'
import iconTuercaNegro from '../../../assets/iconos/iconTuercaNegro.png'
import { useGlobalContext } from '../../../context/GlobalState'
import { useQueryGetUserMe } from '../../../queries/userQueries'
import { OrganizationAPI } from '../../../api/organization.api'
import { BusinessAPI } from '../../../api/business.api'
import { useSidebarContext } from '../sideBar/SidebarContext'
import { useUpdateDataAdministracion } from '../../../pages/useAdministracion.hooks'
import { sileo } from 'sileo'
import ResizableModal from '../../modules/ui/ResizableModal'
import { DashboardAPI } from '../../../api/dashboard.api'
import { UserAuth } from '../../../context/AuthContext'
import { BaseHTTP } from '../../../api/base.http'
import { storeBackendToken } from '../../../utils/authSession'
import { useQueryClient } from '@tanstack/react-query'
import { QueryKeys } from '../../../queries/querykeys'
import { FacturaAPI } from '../../../api/facturas.api'
import getTenantId from '../../utils/getTenantId'

const CustomTooltip = props => (
  <Tooltip
    arrow
    componentsProps={{
      tooltip: {
        sx: {
          bgcolor: '#000033',
          color: 'white',
          fontSize: '0.75rem',
          borderRadius: '6px',
          px: 1.5,
          py: 0.5
        }
      },
      arrow: { sx: { color: '#000033' } }
    }}
    {...props}
  />
)

export const Header = () => {
  const {
    business,
    currentBusiness,
    setCurrentBusiness,
    setMasterEntityData,
    ui: { loader, companyCreationIndicator }
  } = useGlobalContext()
  const { data: userMe } = useQueryGetUserMe()
  const { token, setToken } = UserAuth()
  const [isOpen, setIsOpen] = useState(false)
  const [typeModal, setTypeModal] = useState('')
  const [, setHasNotifications] = useState(false)
  const [isUpdatingDashboard, setIsUpdatingDashboard] = useState(false)
  const organizationAPI = new OrganizationAPI()
  const dashboardAPI = new DashboardAPI()
  const queryClient = useQueryClient()
  const setUpdateData = useUpdateDataAdministracion(state => state.setUpdateData)
  const { isExpanded, isPinned } = useSidebarContext()
  const location = useLocation()

  const getActiveOrganizationId = useCallback(() => {
    if (!token) return null
    try {
      const organizationId = Number(getTenantId(token))
      return Number.isFinite(organizationId) ? organizationId : null
    } catch {
      return null
    }
  }, [token])

  const applyNewToken = newToken => {
    if (!newToken) throw new Error('No se recibió un token para la organización seleccionada')
    BaseHTTP.getInstance().setBackendToken(newToken)
    setToken(newToken)
    storeBackendToken(newToken)
  }

  const resetPlansAndPayments = async () => {
    await Promise.all(
      [
        QueryKeys.GET_PAYMENT_PLANS,
        QueryKeys.GET_PAYMENT_CARDS,
        QueryKeys.GET_PAYMENTS,
        QueryKeys.GET_ORGANIZATION_SUBSCRIPTIONS,
        QueryKeys.GET_ORGANIZATION_SUBSCRIPTION_STATUS,
        QueryKeys.GET_ADDONS,
        QueryKeys.GET_ADDON_SUBSCRIPTIONS,
        QueryKeys.GET_ORGANIZATION_TOKENS_AVAILABLE
      ].map(key => queryClient.resetQueries({ queryKey: [key] }))
    )
  }

  const ensureOrganizationToken = async selectedBusiness => {
    const organizationId = Number(selectedBusiness?.organizationId)
    if (!Number.isFinite(organizationId)) {
      throw new Error('La empresa seleccionada no tiene una organización válida')
    }
    if (getActiveOrganizationId() === organizationId) return false

    const { token: newToken } = await organizationAPI.changeMyCurrentBusiness(organizationId)
    applyNewToken(newToken)
    await resetPlansAndPayments()
    await queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_BUSINESSES_ALL] })
    await queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_USER_ME] })
    return true
  }

  // Notificaciones
  useEffect(() => {
    if (!currentBusiness?.id) return
    const fetchNotification = async () => {
      try {
        const resp = await organizationAPI.getNotification({ businessId: currentBusiness.id })
        setHasNotifications(Array.isArray(resp.data) && resp.data.length > 0)
      } catch (e) {
        console.error(e)
      }
    }
    fetchNotification()
  }, [currentBusiness?.id])

  useEffect(() => {
    if (!currentBusiness?.id) return
    new BusinessAPI().recordAccess(currentBusiness.id).catch(error => console.error(error))
  }, [currentBusiness?.id])

  // Persistir selección
  useEffect(() => {
    if (!token) return
    const stored = localStorage.getItem('selectedBusiness')
    if (!stored) return

    try {
      const storedBusiness = JSON.parse(stored)
      const storedOrganizationId = Number(storedBusiness?.organizationId)
      if (storedOrganizationId !== getActiveOrganizationId()) {
        localStorage.removeItem('selectedBusiness')
        return
      }
      setCurrentBusiness(storedBusiness)
    } catch {
      localStorage.removeItem('selectedBusiness')
    }
  }, [getActiveOrganizationId, token, setCurrentBusiness])
  useEffect(() => {
    if (currentBusiness) {
      localStorage.setItem('selectedBusiness', JSON.stringify(currentBusiness))
    }
  }, [currentBusiness])

  // Obtener masterEntityData al cambiar de empresa
  useEffect(() => {
    const rut = currentBusiness?.rut
    if (!rut) {
      setMasterEntityData(null)
      return
    }

    const fetchMasterId = async () => {
      try {
        const api = new FacturaAPI()
        const resp = await api.getSearchEntityRut(rut)
        if (resp.data?.id) {
          setMasterEntityData(resp.data)
        }
      } catch (error) {
        console.error('Error fetching masterEntityId:', error)
        setMasterEntityData(null)
      }
    }

    fetchMasterId()
  }, [currentBusiness?.rut, setMasterEntityData])

  const handleBusinessChange = async newBusiness => {
    if (!newBusiness || typeof newBusiness !== 'object') return
    if (!newBusiness.id) return

    const organizationId = Number(newBusiness.organizationId)
    const organizationChanged =
      Number.isFinite(organizationId) && organizationId !== getActiveOrganizationId()
    if (currentBusiness?.id === newBusiness.id && !organizationChanged) return

    const updateSelection = async () => {
      if (organizationChanged) {
        await ensureOrganizationToken(newBusiness)
      } else {
        await resetPlansAndPayments()
      }
      setCurrentBusiness(newBusiness)
    }

    if (organizationChanged) {
      await sileo.promise(updateSelection(), {
        loading: { title: 'Cambiando empresa' },
        success: {
          title: 'Empresa Actualizada',
          description: (
            <span className='text-green-500/80 font-medium'>
              Te has movido exitosamente a la nueva organización.
            </span>
          )
        },
        error: {
          title: 'Error de Cambio',
          description: 'No pudimos procesar el cambio de empresa en este momento.'
        }
      })
      return
    }

    await updateSelection()
  }

  const handleRegenerateDashboard = async () => {
    if (isUpdatingDashboard) return
    if (!currentBusiness?.id) {
      console.error('currentBusiness indefinido')
      return
    }

    const businessId = currentBusiness.id
    const updateData = async () => {
      await ensureOrganizationToken(currentBusiness)
      await organizationAPI.getUpdatePreguntonSingleBusiness(businessId)
      const response = await dashboardAPI.regenerateDashboard(businessId)

      setUpdateData(true)
      window.dispatchEvent(
        new CustomEvent('dashboardRegenerated', {
          detail: { businessId, data: response.data }
        })
      )
    }

    setIsUpdatingDashboard(true)
    try {
      await sileo.promise(updateData(), {
        loading: { title: 'Actualizando' },
        success: {
          title: 'Dashboard actualizado',
          description: (
            <span className='text-green-500/80 font-medium'>
              La actualización del SII continuará en segundo plano.
            </span>
          )
        },
        error: {
          title: 'Fallo en Sincronización',
          description: 'Hubo un problema al intentar actualizar los datos.'
        }
      })
    } finally {
      setIsUpdatingDashboard(false)
    }
  }

  const selectNotification = type => {
    setTypeModal(type === 'notification' ? 'notification' : '')
    setIsOpen(true)
  }

  const NavbarBarWithSearchInput = () => (
    <div
      className={`transition-all duration-300 fixed z-20 right-0 w-[calc(100%-0.3rem)] pl-0 ${
        isExpanded || isPinned ? 'sm:pl-64' : 'sm:pl-20'
      }`}
    >
      {!business ? (
        <Skeleton variant='rectangular' width='100%' height={80} />
      ) : (
        <nav className='flex flex-col sm:flex-row items-end sm:items-center justify-end px-4 sm:px-14 bg-slate-50 py-3 sm:py-4 gap-2 sm:gap-0 font-sans text-text'>
          <div className='flex flex-col sm:flex-row items-end sm:items-center gap-2 sm:gap-4 w-full sm:w-auto ml-auto'>
            <div className='flex items-center gap-2'>
              <AutoCompleteHeader
                businessData={business}
                currentBusiness={currentBusiness}
                onClick={handleBusinessChange}
              />
            </div>
            <section className='flex items-center justify-end w-full sm:w-auto gap-3 sm:gap-5 ml-0 sm:ml-4'>
              {companyCreationIndicator?.visible && (
                <div className='flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-600 whitespace-nowrap'>
                  {companyCreationIndicator.status === 'creating' ? (
                    <CircularProgress size={12} sx={{ color: '#0ea5a5' }} />
                  ) : companyCreationIndicator.status === 'success' ? (
                    <span className='text-green-600'>●</span>
                  ) : companyCreationIndicator.status === 'error' ? (
                    <span className='text-red-600'>●</span>
                  ) : null}
                  <span>{companyCreationIndicator.text}</span>
                </div>
              )}
              <button
                onClick={() => selectNotification('')}
                type='button'
                className='p-2 w-9 h-9 hover:scale-110 transition-transform'
              >
                <img src={iconTuercaNegro} alt='gear-icon' />
              </button>
              <CustomTooltip title={isUpdatingDashboard ? 'Actualizando' : 'Actualizar'}>
                <button
                  type='button'
                  onClick={handleRegenerateDashboard}
                  disabled={isUpdatingDashboard}
                  aria-label={isUpdatingDashboard ? 'Actualizando dashboard' : 'Actualizar dashboard'}
                  className={`bg-white text-slate-400 rounded-full shadow-sm p-1 transition-colors ${
                    isUpdatingDashboard
                      ? 'cursor-not-allowed opacity-60'
                      : 'hover:bg-slate-100 cursor-pointer'
                  }`}
                >
                  <UpdateIcon className={isUpdatingDashboard ? 'animate-spin' : ''} />
                </button>
              </CustomTooltip>
            </section>
          </div>
        </nav>
      )}
    </div>
  )

  return (
    <header>
      {/* Global loader overlay */}
      {loader.visible && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex flex-col items-center justify-center z-50'>
          <CircularProgress size={48} color='inherit' />
          <p className='mt-2 text-white'>{loader.text}</p>
        </div>
      )}

      <ResizableModal open={isOpen} onClose={() => setIsOpen(false)}>
        {typeModal === 'notification' ? null : <Settings onClose={() => setIsOpen(false)} />}
      </ResizableModal>
      <NavbarBarWithSearchInput />
      {(location.pathname === '/' || location.pathname === '/BusinessInsights') &&
        currentBusiness && (
          <CardCompanyInfo
            isLoading={false}
            loggedInUser={userMe}
            currentBusiness={currentBusiness}
          />
        )}
    </header>
  )
}

const CardCompanyInfo = ({ isLoading, loggedInUser, currentBusiness }) => {
  if (!currentBusiness) return null
  return <div className='pt-6 pb-6 px-14 mx-auto'>{/* contenido visible */}</div>
}
